import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AllUsersResponse,
  CreateExternalUserRequest,
  CreateExternalUserResponse,
  UpdateExternalUserRequest,
  UpdateExternalUserRoleMappingRequest,
  BasicStatusResponse,
  RoleUserMappingsResponse
} from '../interfaces/api-responses';

export type ExternalRoleName =
  | 'Admin'
  | 'Grower'
  | 'Transloader'
  | 'Shunting'
  | 'Scheduler'
  | 'Gatehouse'
  | 'Receiver'
  | 'Audit';

@Injectable({ providedIn: 'root' })
export class UsersService {
  constructor(private readonly http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `${tokenType} ${token}`;
    return new HttpHeaders(headers);
  }

  async getAllUsers(): Promise<AllUsersResponse> {
    const url = `${environment.apiBaseUrl}/api/v1/users`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.get<AllUsersResponse>(url, { headers }));
  }

  async getUsersByRole(role: ExternalRoleName): Promise<AllUsersResponse> {
    // Map frontend role names to backend role IDs
    // Role IDs: 1=Admin, 2=Grower, 3=Transloader, 4=Receiver, 5=Shunting, 6=Scheduler, 7=Gatehouse, 8=Audit
    const roleIdMap: Record<ExternalRoleName, number> = {
      Admin: 1,
      Grower: 2,
      Transloader: 3,
      Receiver: 4,
      Shunting: 5,
      Scheduler: 6,
      Gatehouse: 7,
      Audit: 8
    };
    const roleId = roleIdMap[role];
    const url = `${environment.apiBaseUrl}/api/v1/users/role?roleId=${roleId}`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.get<AllUsersResponse>(url, { headers }));
  }

  async getUsersByRoleMappings(
    role: ExternalRoleName,
    options?: { includeInactive?: boolean }
  ): Promise<RoleUserMappingsResponse> {
    const roleIdMap: Record<ExternalRoleName, number> = {
      Admin: 1,
      Grower: 2,
      Transloader: 3,
      Receiver: 4,
      Shunting: 5,
      Scheduler: 6,
      Gatehouse: 7,
      Audit: 8
    };
    const roleId = roleIdMap[role];
    const includeInactive = options?.includeInactive ?? false;
    const params = [`roleId=${roleId}`];
    if (includeInactive) params.push('includeInactive=true');
    const url = `${environment.apiBaseUrl}/api/v1/users/role-mappings?${params.join('&')}`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.get<RoleUserMappingsResponse>(url, { headers }));
  }

  async createExternalUser(body: CreateExternalUserRequest): Promise<CreateExternalUserResponse> {
    const url = `${environment.apiBaseUrl}/api/v1/external-users`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.post<CreateExternalUserResponse>(url, body, { headers }));
  }

  async updateExternalUser(userId: number, body: UpdateExternalUserRequest): Promise<BasicStatusResponse> {
    const url = `${environment.apiBaseUrl}/api/v1/external-users/${userId}`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.put<BasicStatusResponse>(url, body, { headers }));
  }

  async updateExternalUserRoleMapping(
    userId: number,
    body: UpdateExternalUserRoleMappingRequest
  ): Promise<BasicStatusResponse> {
    const url = `${environment.apiBaseUrl}/api/v1/external-users/${userId}/mapping`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.put<BasicStatusResponse>(url, body, { headers }));
  }

  async deactivateUser(userId: number, roleId?: number, plantId?: number): Promise<BasicStatusResponse> {
    const params: string[] = [];
    if (roleId !== undefined && roleId > 0) params.push(`roleId=${roleId}`);
    if (plantId !== undefined && plantId > 0) params.push(`plantId=${plantId}`);
    const query = params.length > 0 ? `?${params.join('&')}` : '';
    const url = `${environment.apiBaseUrl}/api/v1/users/${userId}/deactivate${query}`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.put<BasicStatusResponse>(url, {}, { headers }));
  }

  async activateUser(userId: number, roleId: number, mappingId?: number): Promise<BasicStatusResponse> {
    const params = [`roleId=${roleId}`];
    if (mappingId !== undefined && mappingId > 0) params.push(`mappingId=${mappingId}`);
    const url = `${environment.apiBaseUrl}/api/v1/users/${userId}/activate?${params.join('&')}`;
    const headers = this.getAuthHeaders();
    return await firstValueFrom(this.http.put<BasicStatusResponse>(url, {}, { headers }));
  }
}


